import { 
  Box, 
  Paper, 
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import GradingIcon from '@mui/icons-material/Grading';

interface Activity {
  id: number;
  member_name: string;
  quiz_title: string;
  status: string;
  score?: number;
  total_questions?: number;
  percentage?: string | number;
  xp_awarded?: number;
  sent_at: string;
  graded_at?: string;
}

interface RecentActivityProps {
  activities: Activity[];
  title?: string;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'graded':
      return <CheckCircleIcon sx={{ fontSize: 18, color: '#10B981' }} />;
    case 'sent':
      return <SendIcon sx={{ fontSize: 18, color: '#0099CC' }} />;
    case 'answered':
      return <GradingIcon sx={{ fontSize: 18, color: '#F59E0B' }} />;
    default:
      return null;
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'graded': return 'Completed';
    case 'sent': return 'Pending';
    case 'answered': return 'Needs Grading';
    default: return status;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'graded': return { bg: '#D1FAE5', text: '#059669' };
    case 'sent': return { bg: '#DBEAFE', text: '#2563EB' };
    case 'answered': return { bg: '#FEF3C7', text: '#D97706' };
    default: return { bg: '#E5E7EB', text: '#6B7280' };
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const RecentActivity = ({ activities, title = 'Recent Activity' }: RecentActivityProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon sx={{ color: '#6B7280' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2C3E50' }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
          Latest quiz activity across your team
        </Typography>
      </Box>

      <Stack spacing={0} sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {activities.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
              No recent activity
            </Typography>
          </Box>
        ) : (
          activities.map((activity, index) => {
            const colors = getStatusColor(activity.status);
            return (
              <Stack
                key={activity.id || index}
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  py: 2,
                  px: 3,
                  borderBottom: index < activities.length - 1 ? '1px solid #F3F4F6' : 'none',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                }}
              >
                <Box 
                  sx={{ 
                    p: 1, 
                    borderRadius: '8px', 
                    backgroundColor: colors.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getStatusIcon(activity.status)}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#2C3E50',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {activity.member_name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#9CA3AF',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    }}
                  >
                    {activity.quiz_title}
                  </Typography>
                </Box>

                <Stack alignItems="flex-end" spacing={0.5}>
                  <Chip
                    label={
                      activity.status === 'graded' 
                        ? `${activity.score}/${activity.total_questions} • ${activity.xp_awarded} XP`
                        : getStatusLabel(activity.status)
                    }
                    size="small"
                    sx={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      fontWeight: 500,
                      fontSize: '0.7rem',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                    {formatDate(activity.graded_at || activity.sent_at)}
                  </Typography>
                </Stack>
              </Stack>
            );
          })
        )}
      </Stack>
    </Paper>
  );
};
